import { Injectable } from '@angular/core';
import { StaffItem } from '../models/ydata.model';
import { TaskItem, isBreakEventText } from '../models/task.model';
import { SchedulerEventsService } from './scheduler-events.service';

@Injectable({
  providedIn: 'root'
})
export class BreakValidationService {

  constructor(private schedulerEventsService: SchedulerEventsService) { }

  /**
   * Checks if a staff member has their required break event scheduled
   * @param staff The staff member to check
   * @returns true if break is missing, false if break is properly scheduled or not required
   */
  isBreakMissing(staff: StaffItem): boolean {
    // If no break is required, return false (no missing break)
    if (!staff.break_minutes || staff.break_minutes === 0) {
      return false;
    }

    // Get all events for this staff member (no date filtering needed for single-day scheduler)
    const allEvents = this.schedulerEventsService.getAllEvents();
    const staffEvents = allEvents.filter(event => {
      const eventCarerString = String(event.carer_string);
      
      // Check if this event is assigned to this staff member
      return eventCarerString === String(staff.key) || 
             (eventCarerString.includes(',') && eventCarerString.split(',').includes(String(staff.key)));
    });

    // Determine the required break event text based on break minutes
    const requiredBreakText = this.getRequiredBreakText(staff.break_minutes);
    
    // Check if the required break event exists
    const hasRequiredBreak = staffEvents.some(event => 
      this.isBreakEvent(event) && event.text === requiredBreakText
    );

    return !hasRequiredBreak;
  }

  /**
   * Determines the required break event text based on break minutes
   * @param breakMinutes The number of break minutes
   * @returns The expected break event text
   */
  private getRequiredBreakText(breakMinutes: number): string {
    switch (breakMinutes) {
      case 10:
        return 'Staff - Break 10 min paid';
      case 30:
        return 'Staff - Break 30 min unpaid';
      default:
        return '';
    }
  }

  /**
   * Checks if an event is a staff break event
   * @param event The event to check
   * @returns true if the event is a staff break event
   */
  private isBreakEvent(event: TaskItem): boolean {
    return isBreakEventText(event.text);
  }

  /**
   * Gets all staff members who are missing their required breaks
   * @param staffList List of all staff members
   * @returns Array of staff member keys who are missing breaks
   */
  getStaffMissingBreaks(staffList: StaffItem[]): string[] {
    return staffList
      .filter(staff => this.isBreakMissing(staff))
      .map(staff => String(staff.key));
  }
}